import { c as _c } from "react/compiler-runtime";
import { useToast } from '@onefootprint/ui';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';
import { getLogger } from '@/idv/utils';
import { useImgProcessorsContext } from '../../components/image-processors';
import { bytesToMegabytes, hasFileReaderSupport, isFileOrBlob, isString } from '../../utils/capture';
import compressImage, { COMPRESS_EXTRA_MAX_SIZE_MB } from './utils/compress-image';
import imageFileToStrippedBase64 from './utils/image-file-to-stripped-base64';
import resizeImage from './utils/resize-image';
type ProcessedImageFile = {
  file: File | Blob;
  extraCompressed: boolean;
};
type HandleError = (step: ImageProcessingStepError, error?: unknown) => void;
type ImageProcessors = ReturnType<typeof useImgProcessorsContext>;
enum ImageProcessingStepError {
  heic = 'heic',
  resize = 'resize',
  compress = 'compress',
  other = 'other',
  final = 'final',
}
export const DEFAULT_ACCEPTED_FILE_FORMATS = ['.jpg', '.jpeg', 'image/jpeg', '.png', 'image/png', '.webp', 'image/webp', '.bmp', 'image/bmp'];
export enum ImageFileOrUrlProcessingError {
  heicNotSupported = 'heicNotSupported',
  compressionFailed = 'compressionFailed',
  other = 'other',
}
export type ProcessingErrorResult = {
  error: ImageFileOrUrlProcessingError;
};
export function isProcessingResultError(result: ProcessedImageFile | ProcessingErrorResult): result is ProcessingErrorResult {
  return 'error' in result;
}
const isError = (x: unknown): x is Error => x instanceof Error;
const isHeicType = (x: unknown) => x === 'image/heic' || x === 'image/heif';
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'use-process-image'
});
const stringify = (x: unknown): string => {
  try {
    return isString(x) ? x : JSON.stringify(x);
  } catch (e: unknown) {
    if (isError(e)) {
      logWarn(`Error during JSON.stringify: ${e?.message}`, e);
    }
    return '';
  }
};
const convertImageFileToStrippedBase64 = async (onError: HandleError, file: File): Promise<string | undefined> => {
  try {
    return await imageFileToStrippedBase64(file);
  } catch (error) {
    onError(ImageProcessingStepError.other, error);
    return undefined;
  }
};
const stepHeicConversion = async (onError: HandleError, imageProcessors: ImageProcessors | undefined, file: File): Promise<File | Error> => {
  try {
    if (imageProcessors?.heic && !imageProcessors.heicLoading) {
      const res = await imageProcessors.heic({
        blob: file,
        toType: 'image/jpeg',
        quality: 0.8
      });
      const blob = Array.isArray(res) ? res : [res];
      return new File(blob, file.name, {
        type: 'image/jpeg',
        lastModified: file.lastModified
      });
    }
    logWarn('HEIC image processor not available');
    return new Error('HEIC image processor not available');
  } catch (e: unknown) {
    onError(ImageProcessingStepError.heic, e);
    return isError(e) ? e : new Error('HEIC processing failed');
  }
};
const stepImageResize = async (onError: HandleError, file: File): Promise<File | Blob | Error> => {
  try {
    const resized = await resizeImage(file);
    return resized || new Error('resize processing failed');
  } catch (e) {
    onError(ImageProcessingStepError.resize, e);
    return isError(e) ? e : new Error('resize processing failed');
  }
};
const stepImageCompression = async (onError: HandleError, file: File | Blob, extraCompress: boolean = false): Promise<File | Error> => {
  try {
    const compressed = await compressImage(file, extraCompress);
    return compressed || new Error('compress processing failed');
  } catch (e) {
    onError(ImageProcessingStepError.compress, e);
    return isError(e) ? e : new Error('compress processing failed');
  }
};

/**
 * Processes a file by performing HEIC conversion, image resizing, and compression.
 *
 * @param {HandleError} onError - Function to handle errors.
 * @param {ImageProcessors | undefined} imageProcessors - Image processors configuration.
 * @param {File} file - The input file to be processed.
 * @param {boolean} [extraCompressFlag] - Optional flag for extra compression.
 * @returns {Promise<ProcessedImageFile | undefined>} A Promise that resolves to the processed file
 * or undefined if an error occurs during processing.
 *
 * @typedef {Object} ProcessedImageFile - Object containing the processed file and extra compression flag.
 * @property {File} file - The processed file.
 * @property {boolean} extraCompressed - Indicates if extra compression was applied.
 */
const normalizeFileType = async (file: File): Promise<File> => {
  try {
    const {
      fileTypeFromBuffer
    } = await import('file-type');
    const buffer = new Uint8Array(await file.slice(0, 4100).arrayBuffer());
    const detected = await fileTypeFromBuffer(buffer);
    if (!detected || detected.mime === file.type) {
      return file;
    }
    logWarn(`MIME mismatch: browser reported ${file.type}, magic bytes detected ${detected.mime}`);
    return new File([file], file.name, {
      type: detected.mime,
      lastModified: file.lastModified
    });
  } catch (error) {
    logWarn(`Magic-byte detection failed, proceeding with browser-reported type: ${file.type}`, error);
    return file;
  }
};
const runProcessFileScript = async (onError: HandleError, imageProcessors: ImageProcessors | undefined, file: File, extraCompressFlag?: boolean): Promise<ProcessedImageFile | ProcessingErrorResult> => {
  // Reported at every exit, so upload volume by file type stays countable on all paths.
  const source = `${bytesToMegabytes(file.size)} MB ${file.type}`;
  const correctedFile = await normalizeFileType(file);
  if (correctedFile.type === 'application/pdf') {
    logInfo(`file processed: ${source} -> pdf, passed through unmodified`);
    return {
      file: correctedFile,
      extraCompressed: false
    };
  }
  const heicOutput = isHeicType(correctedFile.type) ? await stepHeicConversion(onError, imageProcessors, correctedFile) : correctedFile;
  if (isError(heicOutput)) {
    logWarn('HEIC conversion failed: halting processing');
    return {
      error: ImageFileOrUrlProcessingError.heicNotSupported
    };
  }
  const resizeInput = isFileOrBlob(heicOutput) ? heicOutput : correctedFile;
  let resizeOutput: File | Blob | Error;
  // resizeImage is not supported for any file type other than the ones in DEFAULT_ACCEPTED_FILE_FORMATS. The promise is never resolved or rejected
  if (DEFAULT_ACCEPTED_FILE_FORMATS.includes(resizeInput.type)) {
    resizeOutput = hasFileReaderSupport() ? await stepImageResize(onError, resizeInput) : new Error('Unsupported FileReader in this browser');
  } else {
    resizeOutput = resizeInput;
  }
  if (isError(resizeOutput)) {
    logWarn(`Proceeding with not resized image: ${stringify(resizeOutput)}`);
  }
  const prevFile = [resizeOutput, heicOutput].find(isFileOrBlob);
  const compressionInput = prevFile && !isError(prevFile) ? prevFile : correctedFile;
  const compressOutput = await stepImageCompression(onError, compressionInput, extraCompressFlag);
  if (isError(compressOutput)) {
    logWarn(`Proceeding with uncompressed image: ${stringify(compressOutput)}`);
  }
  const extraCompressed = !isError(resizeOutput) && !isError(compressOutput) && resizeOutput.size > compressOutput.size && Boolean(extraCompressFlag) && compressOutput.size <= COMPRESS_EXTRA_MAX_SIZE_MB;
  const sizeInMb = (output: File | Blob | Error) => isError(output) ? 'n/a' : bytesToMegabytes(output.size);
  logInfo(`file processed: ${source} -> resized ${sizeInMb(resizeOutput)} MB -> compressed ${sizeInMb(compressOutput)} MB, extra compressed: ${extraCompressed}`);
  const final = [compressOutput, resizeOutput, heicOutput].find(isFileOrBlob);
  if (!final || isError(final)) {
    onError(ImageProcessingStepError.final);
  }
  return final && !isError(final) ? {
    file: final,
    extraCompressed
  } : {
    error: ImageFileOrUrlProcessingError.other
  };
};
const processImageUrl = async (onError: HandleError, url: string, extraCompress?: boolean): Promise<ProcessedImageFile | ProcessingErrorResult> => {
  let file: File | undefined;
  try {
    file = await imageCompression.getFilefromDataUrl(url, 'imageFileName');
  } catch (error) {
    onError(ImageProcessingStepError.other, error);
  }
  if (!file) {
    logWarn('Image file is undefined after image compression');
    return {
      error: ImageFileOrUrlProcessingError.other
    };
  }
  logInfo(`(getFilefromDataUrl) file from url: ${bytesToMegabytes(file.size)} MB ${file.type}`);
  const output = await runProcessFileScript(onError, undefined, file, extraCompress);
  return output;
};
const useProcessImage = options => {
  const $0 = _c(19);
  const {
    toast
  } = useToast();
  const imageProcessors = useImgProcessorsContext();
  let t0;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "document-flow.components.image-file-url-processing-errors"
    };
    $0[0] = t0;
  } else {
    t0 = $0[0];
  }
  const {
    t
  } = useTranslation("idv", t0);
  let t1;
  if ($0[1] !== t || $0[2] !== toast) {
    t1 = (step, error) => {
      if (error) {
        logWarn(stringify(error), error);
      }
      toast({
        title: "Uh-oh",
        description: t($ => $[step])
      });
    };
    $0[1] = t;
    $0[2] = toast;
    $0[3] = t1;
  } else {
    t1 = $0[3];
  }
  const handleError = t1;
  let acceptedFileFormats;
  if ($0[4] !== imageProcessors?.heic || $0[5] !== options?.allowPdf) {
    acceptedFileFormats = [...DEFAULT_ACCEPTED_FILE_FORMATS];
    if (imageProcessors?.heic) {
      acceptedFileFormats.push(".heic", ".heif", "image/heic", "image/heif");
    }
    if (options?.allowPdf) {
      acceptedFileFormats.push(".pdf", "application/pdf");
    }
    $0[4] = imageProcessors?.heic;
    $0[5] = options?.allowPdf;
    $0[6] = acceptedFileFormats;
  } else {
    acceptedFileFormats = $0[6];
  }
  let t2;
  if ($0[7] !== handleError) {
    t2 = file => convertImageFileToStrippedBase64(handleError, file);
    $0[7] = handleError;
    $0[8] = t2;
  } else {
    t2 = $0[8];
  }
  let t3;
  if ($0[9] !== handleError || $0[10] !== imageProcessors) {
    t3 = (file_0, extraCompressFlag) => runProcessFileScript(handleError, imageProcessors, file_0, extraCompressFlag);
    $0[9] = handleError;
    $0[10] = imageProcessors;
    $0[11] = t3;
  } else {
    t3 = $0[11];
  }
  let t4;
  if ($0[12] !== handleError) {
    t4 = (url, extraCompress) => processImageUrl(handleError, url, extraCompress);
    $0[12] = handleError;
    $0[13] = t4;
  } else {
    t4 = $0[13];
  }
  let t5;
  if ($0[14] !== acceptedFileFormats || $0[15] !== t2 || $0[16] !== t3 || $0[17] !== t4) {
    t5 = {
      convertImageFileToStrippedBase64: t2,
      processImageFile: t3,
      processImageUrl: t4,
      acceptedFileFormats
    };
    $0[14] = acceptedFileFormats;
    $0[15] = t2;
    $0[16] = t3;
    $0[17] = t4;
    $0[18] = t5;
  } else {
    t5 = $0[18];
  }
  return t5;
};
export default useProcessImage;